/**
 * features/timetable/RecentChanges.tsx
 * ------------------------------------------------------------------
 * "What moved?" — the companion to the grid.
 *
 * Because both apps share one database, an alteration appears in the portal
 * instantly. But nobody re-opens their timetable on the off-chance, so a
 * silent change is an unattended one. `timetableChanges` is written by the
 * onTimetableSlotWritten trigger; this reads it back.
 *
 * Scoped to the viewer: a teacher sees changes that touch THEIR lessons, a
 * parent changes to their child's class. Someone with canViewAllTimetables can
 * widen it to the whole school, because that is the person who needs to see
 * the shape of a week's edits.
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';
import { History } from 'lucide-react';
import {
  collection, limit, onSnapshot, orderBy, query, where, type Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

interface ChangeDoc {
  id: string;
  slotId: string;
  type: 'created' | 'updated' | 'deleted';
  fields: string[];
  summary: string;
  affectedTeacherIds: string[];
  affectedClasses: string[];
  changedBy: string | null;
  at?: Timestamp;
}

const TYPE_LABEL: Record<ChangeDoc['type'], string> = {
  created: 'Added',
  updated: 'Changed',
  deleted: 'Removed',
};

const TYPE_STYLE: Record<ChangeDoc['type'], string> = {
  created: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
  updated: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  deleted: 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200',
};

export default function RecentChanges({
  teacherId, className, showAll,
}: {
  /** Show only changes touching this teacher. */
  teacherId?: string | null;
  /** Show only changes touching this class. */
  className?: string | null;
  /** Whole school — for staff who can look anything up. */
  showAll?: boolean;
}) {
  const [changes, setChanges] = useState<ChangeDoc[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setChanges(null);
    setError(false);

    // array-contains cannot be combined with a second array-contains, so the
    // scope is ONE filter. Whole-school takes no filter at all.
    const base = collection(db, 'timetableChanges');
    const q = showAll
      ? query(base, orderBy('at', 'desc'), limit(40))
      : teacherId
        ? query(base, where('affectedTeacherIds', 'array-contains', teacherId), orderBy('at', 'desc'), limit(20))
        : className
          ? query(base, where('affectedClasses', 'array-contains', className), orderBy('at', 'desc'), limit(20))
          : null;

    if (!q) { setChanges([]); return; }

    return onSnapshot(
      q,
      (snap) => setChanges(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ChangeDoc, 'id'>) }))),
      (e) => {
        // A missing composite index is the likely cause and is not worth
        // breaking the page over — the grid above it is the main event.
        console.error('timetable changes:', e);
        setError(true);
      },
    );
  }, [teacherId, className, showAll]);

  if (error) return null;
  if (changes === null) {
    return <div className="py-4 text-center text-xs text-[var(--sp-muted)]">Loading recent changes…</div>;
  }
  if (!changes.length) {
    return (
      <div className="rounded border border-dashed border-[var(--sp-line-2)] px-4 py-6 text-center text-xs text-[var(--sp-muted)]">
        No timetable changes recorded yet.
      </div>
    );
  }

  return (
    <div className="rounded border border-[var(--sp-line)]">
      <div className="flex items-center gap-2 border-b border-[var(--sp-line)] px-4 py-2.5">
        <History className="h-4 w-4 text-[var(--sp-muted)]" />
        <span className="text-sm font-semibold">Recent changes</span>
        <span className="text-xs text-[var(--sp-muted)]">
          {showAll ? 'whole school' : teacherId ? 'affecting your lessons' : 'affecting this class'}
        </span>
      </div>
      <ul className="divide-y divide-[var(--sp-line)]">
        {changes.map((c) => (
          <li key={c.id} className="flex items-start gap-3 px-4 py-2.5">
            <span className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${TYPE_STYLE[c.type]}`}>
              {TYPE_LABEL[c.type]}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm">{c.summary}</div>
              <div className="text-[11px] text-[var(--sp-muted)]">
                {c.at?.toDate ? c.at.toDate().toLocaleString() : 'just now'}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
